import { useState, useEffect, useRef } from "react";
import { addFriend, sendNotification } from "../api/users";
import useBookSearch from "../hooks/useBookSearch";
import { getPlanetStyle, isNewToday } from "../utils/format";

const StardustSection = ({ users, allBooks, currentUser, autoFriends, myFriends, reloadFriends, onUserClick, onBookClick, onExternalBookClick, showUserSearch, setShowUserSearch, showBookWorld, setShowBookWorld }) => {
  const [userSearch, setUserSearch] = useState("");
  const [bookWorldSearch, setBookWorldSearch] = useState("");
  const [bookWorldQuery, setBookWorldQuery] = useState("");
  const { results: bookWorldResults, loading: bookWorldLoading } = useBookSearch(bookWorldQuery);

  const uid = currentUser?.uid;

  // 패널이 열려도 화면 아래에 있으면 열린 줄 모른다.
  // 열릴 때 그 자리로 옮기고 입력창에 커서를 둔다.
  const userPanelRef = useRef(null);
  const userInputRef = useRef(null);
  const bookPanelRef = useRef(null);
  const bookInputRef = useRef(null);

  /* 마우스로 쓰는 기기인가. 그런 기기는 키보드가 이미 책상 위에 있어서
     커서를 두어도 화면이 가려지지 않는다. 손가락으로 쓰는 기기는 커서를
     두는 순간 키보드가 화면 절반을 차지한다. */
  const hasMouse = window.matchMedia?.("(hover: hover) and (pointer: fine)")?.matches;

  const revealPanel = (panel, input, focusInput) => {
    if (!panel) return undefined;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    panel.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "center" });
    if (!focusInput) return undefined;
    // 스크롤이 끝난 뒤 포커스해야 모바일에서 키보드가 화면을 가리며 튀지 않는다
    const t = setTimeout(() => input?.focus(), reduce ? 0 : 320);
    return () => clearTimeout(t);
  };

  useEffect(() => {
    if (!showUserSearch) return undefined;
    /* '다른 행성 보기' 는 사람 목록을 보러 여는 자리다. 손가락 기기에서
       커서를 두면 키보드가 올라와 방금 연 그 목록을 그대로 덮는다. 여기
       검색칸은 목록을 거르는 칸이지 이 화면의 목적이 아니다. */
    return revealPanel(userPanelRef.current, userInputRef.current, hasMouse);
  }, [showUserSearch, hasMouse]);

  useEffect(() => {
    if (!showBookWorld) return undefined;
    /* 책 찾기는 다르다. 글자를 치는 것 말고 할 일이 없어서, 커서를 두는
       편이 한 번 덜 누르게 한다. */
    return revealPanel(bookPanelRef.current, bookInputRef.current, true);
  }, [showBookWorld]);

  const submitBookWorldSearch = () => {
    if (bookWorldSearch.trim().length >= 1) setBookWorldQuery(bookWorldSearch.trim());
  };

  return (
    <>
      {showUserSearch && (() => {
        const otherUsers = (users || []).filter(u => u.id !== uid);
        const filtered = userSearch.trim()
          ? otherUsers.filter(u =>
              (u.name || "").toLowerCase().includes(userSearch.toLowerCase()) ||
              (u.handle || "").toLowerCase().includes(userSearch.toLowerCase())
            )
          : otherUsers;
        return (
          <div className="user-search-panel" ref={userPanelRef}>
            <div className="user-search-header">
              {/* 검색 중에는 걸러진 수를, 그냥 열었을 때는 전체를 보여준다.
                  같은 자리에 늘 전체만 두면 '3건 나왔는데 26' 이라 어긋난다. */}
              <span>
                🔭 다른 행성 보기
                <span className="user-search-count">
                  {userSearch.trim() ? `${filtered.length} / ${otherUsers.length}` : otherUsers.length}
                </span>
              </span>
              <button onClick={() => { setShowUserSearch(false); setUserSearch(""); }}>✕</button>
            </div>
            <div className="user-search-input-wrap">
              <input
                ref={userInputRef}
                className="user-search-input"
                placeholder="이름으로 검색..."
                value={userSearch}
                onChange={e => setUserSearch(e.target.value)}
              />
            </div>
            <div className="user-search-results">
              {filtered.length === 0 ? (
                <div className="user-search-empty">검색 결과가 없어요</div>
              ) : (
                filtered.map(u => {
                  const bookCount = (u.bookIds || []).length;
                  const isAutoFriend = autoFriends.some(af => af.id === u.id);
                  const iAdded = myFriends.includes(u.id);
                  const theyAdded = (u.manualFriends || []).includes(currentUser?.uid);
                  const isFriend = isAutoFriend || iAdded || theyAdded;
                  let friendType = null;
                  if (isAutoFriend) friendType = "auto";
                  else if (iAdded && theyAdded) friendType = "mutual";
                  else if (iAdded) friendType = "i-added";
                  else if (theyAdded) friendType = "added-me";
                  return (
                    <div key={u.id} className="user-search-card">
                      <div className="user-search-card-main" onClick={() => { onUserClick(u.id); setShowUserSearch(false); setUserSearch(""); }}>
                        <div className="user-search-orb" style={getPlanetStyle(u)}>
                          {u.avatar || "🌿"}
                        </div>
                        <div className="user-search-info">
                          <div className="user-search-name">
                            {u.name}
                            {/* 오늘 온 사람. 목록 순서는 그대로 두고 표시만 붙인다 —
                                맨 위로 올리면 매일 목록이 뒤바뀌어 아는 얼굴을 못 찾는다. */}
                            {isNewToday(u.createdAt) && <span className="user-new-badge">new</span>}
                          </div>
                          <div className="user-search-meta">📚 {bookCount}권</div>
                        </div>
                      </div>
                      {!currentUser?.isAnonymous && (
                        isFriend ? (
                          <div className="friend-badge-wrap">
                            <span className="btn-add-friend is-friend">✓ 이웃 행성</span>
                            {friendType === "auto" && <span className="friend-badge-sub">취향</span>}
                            {friendType === "mutual" && <span className="friend-badge-sub mutual">서로 추가</span>}
                            {friendType === "i-added" && <span className="friend-badge-sub">내가 추가</span>}
                            {friendType === "added-me" && <span className="friend-badge-sub added-me">나를 추가</span>}
                          </div>
                        ) : (
                          <button
                            className="btn-add-friend"
                            onClick={async (e) => {
                              e.stopPropagation();
                              await addFriend(currentUser.uid, u.id);
                              await sendNotification({ toUserId: u.id, fromUserId: currentUser.uid, type: "friend" });
                              if (reloadFriends) reloadFriends();
                            }}
                          >
                            + 이웃
                          </button>
                        )
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        );
      })()}

      {showBookWorld && (
        <div className="user-search-panel" ref={bookPanelRef}>
          <div className="user-search-header">
            <span>📚 다른 책 탐험하기</span>
            <button onClick={() => { setShowBookWorld(false); setBookWorldSearch(""); setBookWorldQuery(""); }}>✕</button>
          </div>
          <div className="user-search-input-wrap stardust-search-wrap">
            <input
              ref={bookInputRef}
              className="user-search-input stardust-input-flex"
              placeholder="책 제목 또는 저자 검색..."
              value={bookWorldSearch}
              onChange={e => setBookWorldSearch(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") submitBookWorldSearch(); }}
            />
            <button className="btn-book-search" onClick={submitBookWorldSearch} disabled={bookWorldSearch.trim().length < 1}>검색</button>
          </div>
          <div className="user-search-results">
            {!bookWorldQuery ? (
              <div className="user-search-empty">책 제목이나 저자를 입력 후 검색하세요 ✨</div>
            ) : bookWorldLoading ? (
              <div className="user-search-empty">검색 중...</div>
            ) : bookWorldResults.length === 0 ? (
              <div className="user-search-empty">검색 결과가 없어요</div>
            ) : (
              bookWorldResults.filter((b, i, arr) => arr.findIndex(x => x.title === b.title && x.author === b.author) === i).map((b, i) => {
                const existing = allBooks.find(ab => ab.title === b.title && ab.author === b.author);
                const handleClick = () => {
                  setShowBookWorld(false);
                  setBookWorldSearch("");
                  if (existing) {
                    onBookClick(existing.id);
                  } else {
                    const extBook = {
                      id: `ext_${b.googleId || i}`,
                      title: b.title,
                      author: b.author,
                      cover: b.cover,
                      genre: b.genre || "기타",
                      year: b.publishedDate || "",
                      emoji: "📖",
                      bg: "#1a1a2e",
                      memos: [],
                      completedBy: {},
                      emotions: {},
                    };
                    if (onExternalBookClick) onExternalBookClick(extBook);
                  }
                };
                return (
                  <div key={b.googleId || i} className="user-search-card" onClick={handleClick}>
                    <div className="book-world-cover" style={{ background: "#1a1a2e" }}>
                      {b.cover
                        ? <img src={b.cover} alt={b.title} className="book-world-cover-img" />
                        : <span>📖</span>
                      }
                    </div>
                    <div className="user-search-info">
                      <div className="user-search-name">{b.title}</div>
                      <div className="user-search-meta">{b.author}{b.publishedDate ? ` · ${b.publishedDate}` : ""}</div>
                      {existing && <div className="user-search-meta stardust-in-app-label">앱에 등록됨</div>}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default StardustSection;
